'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef } from 'react';
interface UseDialogResetOptions {
  isOpen: boolean;
  onReset: () => void;
  onClose?: () => void;
}

/**
 * Hook for handling dialog reset when opening.
 *
 * Uses a ref to track open state transitions, ensuring reset only runs
 * when the dialog actually opens (not on every render or dependency change).
 *
 * @param isOpen - Current open state of the dialog
 * @param onReset - Callback to reset dialog state (called when dialog opens)
 * @param onClose - Optional callback when dialog closes (for direct control cases)
 *
 * @example
 * // Context-based (e.g., with useMultiStepDialog)
 * const { isOpen } = useMultiStepDialog()
 * useDialogReset({ isOpen, onReset: handleReset })
 *
 * @example
 * // Direct control (component manages isOpen state)
 * const [isOpen, setIsOpen] = useState(false)
 * const { handleOpenChange } = useDialogReset({
 *   isOpen,
 *   onReset: handleReset,
 *   onClose: () => setIsOpen(false)
 * })
 * <Dialog open={isOpen} onOpenChange={handleOpenChange} />
 */
export function useDialogReset(t0) {
  const $ = _c(8);
  const {
    isOpen,
    onReset,
    onClose
  } = t0;
  const wasOpenRef = useRef(false);
  let t1;
  let t2;
  if ($[0] !== isOpen || $[1] !== onReset) {
    t1 = () => {
      const justOpened = isOpen && !wasOpenRef.current;
      wasOpenRef.current = isOpen;
      if (justOpened) {
        onReset();
      }
    };
    t2 = [isOpen, onReset];
    $[0] = isOpen;
    $[1] = onReset;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== onClose) {
    t3 = open => {
      if (!open) {
        onClose?.();
      }
    };
    $[4] = onClose;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const handleOpenChange = t3;
  let t4;
  if ($[6] !== handleOpenChange) {
    t4 = {
      handleOpenChange
    };
    $[6] = handleOpenChange;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}